import {
  Bug,
  GitPullRequest,
  GitBranch,
  FileCode,
  StickyNote,
  Briefcase,
  Pin,
} from 'lucide-react'
import { cn, formatCompactNumber } from '@/lib/utils'
import type { ContextItem } from '@/types/contextObservability'
import { Badge } from '@/components/ui/badge'

export interface ContextItemsListProps {
  items: ContextItem[]
  className?: string
}

const kindIcons: Record<string, React.ReactNode> = {
  workitem: <Bug className="h-4 w-4" />,
  pr: <GitPullRequest className="h-4 w-4" />,
  repo: <GitBranch className="h-4 w-4" />,
  file: <FileCode className="h-4 w-4" />,
  note: <StickyNote className="h-4 w-4" />,
}

const kindLabels: Record<string, string> = {
  workitem: 'Work Item',
  pr: 'Pull Request',
  repo: 'Repository',
  file: 'File',
  note: 'Note',
}

export function ContextItemsList({ items, className }: ContextItemsListProps) {
  if (items.length === 0) {
    return (
      <div className={cn('flex items-center justify-center py-6 text-sm text-muted-foreground', className)}>
        <Briefcase className="h-4 w-4 mr-2 opacity-50" />
        No context items attached
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-start gap-3 rounded-md border px-3 py-2.5 bg-background"
        >
          <span className="mt-0.5 text-muted-foreground flex-shrink-0">
            {item.pinned && <Pin className="h-3 w-3 text-primary absolute -ml-2 -mt-1" />}
            {kindIcons[item.kind] ?? <Briefcase className="h-4 w-4" />}
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{item.title}</span>
              {item.pinned && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  pinned
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
              <span>{kindLabels[item.kind] ?? item.kind}</span>
              {Object.entries(item.metadata).slice(0, 3).map(([key, value]) => (
                <span key={key}>
                  <span className="text-border mx-1">·</span>
                  {value}
                </span>
              ))}
            </div>
          </div>

          <span className="text-xs text-muted-foreground font-mono flex-shrink-0 mt-0.5">
            {formatCompactNumber(item.tokens)} tok
          </span>
        </div>
      ))}
    </div>
  )
}
